import styled, { css } from "styled-components";
import { Flex } from "rebass/styled-components";
import { Link } from "react-router-dom";

import { luminance } from "MVVM/Utilities/Styling";
import {
  ACCENT_BORDER,
  ACCENT_DEEP,
  ACCENT_SOFT,
} from "MVVM/Views/LandingPages/styledComponents";

// Same treatment as the AutoCapture page's primary CTA, so the upgrade action
// reads as the site's marquee button rather than a flat menu row.
const BUTTON_GRADIENT = `linear-gradient(135deg, ${ACCENT_DEEP} 0%, ${luminance(
  ACCENT_DEEP,
  -0.42,
)} 100%)`;

/**
 * Trigger and panel share a width and a horizontal inset, which is what lines
 * the panel up under the avatar instead of hanging off a narrower control.
 */
/**
 * The panel's width. The trigger sizes to its own content and is capped at this
 * instead: at a flat 360px the avatar, name and caret left roughly 160px of
 * dead space, which showed up as an oddly wide hover and open state. Both are
 * right-anchored, so their right edges still line up.
 */
const MENU_WIDTH = "360px";
const MENU_INSET = "12px";

export const Cluster = styled(Flex)`
  align-items: center;
  gap: 12px;
  position: relative;
`;

/**
 * Site accent, not the Unlimited tier's purple. It wore the tier's own colours
 * to read as the thing it sells, but in a strip that already holds a search
 * pill, ghost links, a bell and an avatar, a saturated gradient in a fifth
 * colour was the loudest thing in the row without being the most important.
 * Same treatment as the mega menu CTA, so the two agree.
 */
export const TrialButton = styled.button`
  /* The Unlimited tier's own purple, so the button looks like the thing it
     sells rather than another green CTA. Flat rather than the gradient and glow
     it had: in a strip of ghost links and a search field, that read as the
     loudest element on the page. */
  align-items: center;
  background: ${({ theme }) => theme.colors.tierPlat};
  border: 1px solid transparent;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  gap: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  /* Sits apart from the bell and avatar, which belong together. */
  margin-right: 8px;
  padding: 5px 11px;
  white-space: nowrap;

  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.tierPlatDark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.tierPlat};
    outline-offset: 2px;
  }

  svg {
    height: 14px;
    width: 14px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

/**
 * The strip's own auth pair, rather than the site's generic Button. That
 * component is sized for page bodies and read heavy beside ghost links, a
 * search pill and an avatar.
 *
 * The outline keeps the tertiary Button's treatment, envy border and text that
 * fills on hover, at the strip's size. Padding is 2px lighter than the solid
 * one to absorb the border, so the pair matches in height.
 */
export const AuthSecondary = styled(Link)`
  align-items: center;
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 11px;
  text-decoration: none;
  transition:
    background 0.2s ease,
    color 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const AuthPrimary = styled(Link)`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: 2px solid transparent;
  border-radius: 8px;
  display: flex;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 600;
  padding: 5px 11px;
  text-decoration: none;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

/* Trigger */

/**
 * A compact control, not the panel's lid. It used to be square-topped and
 * stretched to the bar's bottom edge so it read as the panel's header, but that
 * geometry was tuned to the bar row's 18px padding. In the strip there is no
 * row to be flush with, so it is a normal rounded button and the panel below is
 * a normal dropdown.
 */
export const PillTrigger = styled.button<{ $open?: boolean }>`
  align-items: center;
  background: ${({ theme, $open }) =>
    $open ? theme.colors.chalkAlpha08 : "transparent"};
  border: none;
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  gap: 8px;
  max-width: ${MENU_WIDTH};
  padding: 5px ${MENU_INSET};

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
  }

  &:hover span,
  &:hover svg {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  /* PillLabel is hidden here, so the fixed width would be 200px of empty space
     stranding the caret at the screen edge. */
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    gap: 4px;
    padding-left: 8px;
    padding-right: 8px;
    width: auto;
  }
`;

export const PillLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    display: none;
  }
`;

// Tier gradients are set on a Text with its own sizing, so shrink it here.
export const PillTier = styled.span`
  display: block;

  * {
    font-size: 13px;
  }
`;

// Sits on the tier's line, so it matches the tier's size rather than the name's.
export const PillStatus = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  display: block;
  font-size: 13px;
  font-weight: 600;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PillUsername = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  display: block;
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PillCaret = styled.span<{ $open?: boolean }>`
  align-items: center;
  color: ${({ theme, $open }) =>
    $open ? theme.colors.ice : theme.colors.chalk};
  display: flex;
  flex-shrink: 0;

  svg {
    height: 16px;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
    transition: transform 150ms ease;
    width: 16px;
  }
`;

/* Panel */

export const MenuPanel = styled.div`
  background: ${({ theme }) => theme.colors.navy500};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 12px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.35);
  box-sizing: border-box;
  margin-top: 8px;
  padding: ${MENU_INSET};
  position: absolute;
  right: 0;
  top: 100%;
  width: ${MENU_WIDTH};

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: min(${MENU_WIDTH}, calc(100vw - 24px));
  }
  z-index: 120;

  @media (prefers-reduced-motion: no-preference) {
    animation: userMenuIn 120ms ease-out;
  }

  @keyframes userMenuIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
`;

// ClipQuotaBar renders nothing for unlimited plans, so this collapses cleanly.
export const QuotaSlot = styled.div`
  &:not(:empty) {
    margin-bottom: 12px;
  }
`;

/**
 * Always visible now, at every width. These used to be hidden wherever the strip
 * showed them, which meant that on a desktop the account menu had no route to
 * your own profile: the one place people look for it.
 *
 * Tinted and inset so the block reads as yours rather than as more nav.
 */
export const PersonalRows = styled.div`
  /* Same card as ClipQuotaBar's compact container directly above it. */
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 4px 0;
`;

/**
 * Outdented to the panel's own edge rather than lining up with the rows it
 * labels, so the card below reads as sitting under it rather than beside it.
 */
export const SectionLabel = styled.div`
  color: ${({ theme }) => theme.colors.shade400};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 10px 0 6px;
  text-transform: uppercase;
`;

// The link fills the row and the pin sits beside it: a button inside an anchor
// is not valid, and nesting one swallows the navigation.
export const ShortcutRow = styled.div`
  align-items: center;
  display: flex;

  a {
    flex: 1;
    min-width: 0;
  }
`;

export const PinButton = styled.button<{ $pinned: boolean }>`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme, $pinned }) =>
    $pinned ? theme.colors.envy : theme.colors.shade400};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  margin-right: 10px;
  padding: 6px;
  border-radius: 6px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme, $pinned }) =>
      $pinned ? theme.colors.envy : theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  svg {
    height: 14px;
    width: 14px;
  }
`;

export const MenuList = styled.div`
  display: flex;
  flex-direction: column;
`;

const rowStyle = css`
  align-items: center;
  background: none;
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 16px;
  gap: 14px;
  padding: 11px 12px;
  text-align: left;
  text-decoration: none;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
    color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }
`;

export const MenuLink = styled(Link)`
  ${rowStyle}
`;

export const MenuAction = styled.button`
  ${rowStyle}
`;

export const MenuIcon = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-shrink: 0;

  /* Bundled SVG assets carry no fill of their own, so without this they render
     black and vanish on the dark panel. No-op for react-icons glyphs. */
  svg {
    fill: currentColor;
    height: 20px;
    width: 20px;
  }
`;

export const MenuLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

// Nudged inboard of the row's own padding: hard against the panel edge these
// right-aligned values read as falling off it.
export const MenuCount = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  margin-right: 6px;
  font-variant-numeric: tabular-nums;
`;

export const MenuPending = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 14px;
  margin-right: 6px;
  font-weight: 600;
  white-space: nowrap;
`;

export const MenuDivider = styled.div`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  height: 1px;
  margin: 12px 0;
`;

export const MenuPlanFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  margin-top: 10px;
  padding-top: 12px;
`;

export const MenuPlanCta = styled(Link)`
  background: ${BUTTON_GRADIENT};
  border: 1px solid ${ACCENT_BORDER};
  border-radius: 10px;
  box-shadow: 0 8px 30px ${ACCENT_SOFT};
  color: ${({ theme }) => theme.colors.ice};
  display: block;
  font-size: 16px;
  font-weight: 700;
  padding: 14px;
  text-align: center;
  text-decoration: none;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: 0 10px 40px ${ACCENT_BORDER};
    color: ${({ theme }) => theme.colors.ice};
    transform: translateY(-2px);
  }
`;
